/**
 * This component will show the product details summary on product detail page
 *
 * @module views/components/ProductSummary
 * @memberof -Common
 */
import './ProductSummary.scss';

import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { Text as PalText } from 'web-palette/dist/components/Text/Text';

import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { OVERLAY_TYPE, useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import RoundedContainer from '@ulta/components/RoundedContainer/RoundedContainer';
import SummaryCard from '@ulta/components/SummaryCard/SummaryCard';
import SummaryItemCard from '@ulta/components/SummaryItemCard/SummaryItemCard';

/**
 * Represents a ProductSummary component
 *
 * @method
 * @param {ProductSummaryProps} props - React properties passed from composition
 * @returns ProductSummary
 */
export const ProductSummary = function( { title, message, summaryCards, layout, id, variant } ){
  const [selectedSummaryItemCard, setSelectedSummaryItemCard] = useState( null );
  const { openOverlay } = useOverlay();
  const { breakpoint, inflection } = useDeviceInflection();

  /**
   * Handles selection of summary card item.
   * @method handleSelectedSummaryCardItem
   * @param {object} item - Selected Summary Item Card
   */
  const handleSummaryItemCardSelection = ( item ) => {
    setSelectedSummaryItemCard( item );
  };

  /*
  * Renders overlay content for the selected summary card.
  */
  const renderSummaryItemCardOverlay = useMemo( () => {
    if( !selectedSummaryItemCard ){
      return null;
    }
    const { image, title, subTitle, learnMoreAction, description } = selectedSummaryItemCard;

    return (
      <SummaryItemCard
        image={ image }
        title={ title }
        subTitle={ subTitle }
        action={ learnMoreAction }
        description={ description }
      />
    );
  }, [selectedSummaryItemCard] );

  /*
  * Watch for changes to selectedSummaryItemCard and open overlay
  */
  useEffect( () => {
    openSummaryItemCardOverlay(
      {
        selectedSummaryItemCard,
        content: renderSummaryItemCardOverlay,
        breakpoint
      },
      {
        openOverlay,
        setSelectedSummaryItemCard
      }
    );
  }, [selectedSummaryItemCard] );

  return (
    <div className='ProductSummary'
      id={ id }
      role='complementary'
    >
      <div className={
          `${variant === 'Full' ? 'ProductSummary__fullWidth' : ''}` }
      >
        { summaryCards?.length > 0 &&
          <>
            { summaryCards.map( ( summaryCard, i )=>{
              return (
                <SummaryCard
                  key={ `${i}-${summaryCard?.title}` }
                  type={ variant }
                  icon={ summaryCard?.icon }
                  title={ summaryCard?.title }
                  subTitle={ summaryCard?.subTitle }
                  items={ summaryCard?.items }
                  layout={ layout || inflection.DESKTOP ? 'horizontal' : 'vertical' }
                  onItemSelection={ handleSummaryItemCardSelection }
                />
              );
            } )
            }
          </>
        }

        { ( title || message ) && (
          <div className='ProductSummary__summary'>
            { title && (
              <PalText htmlTag='h2' textStyle='title-sm'>
                { title }
              </PalText>
            ) }

            { message && (
              <PalText htmlTag='p' textStyle='body-lg'>
                { message }
              </PalText>
            ) }
          </div>
        ) }
      </div>
    </div>
  );
};

export const openSummaryItemCardOverlay = ( data, methods ) => {
  const { content, selectedSummaryItemCard, breakpoint } = handleEmptyObjects( data );
  const { openOverlay, setSelectedSummaryItemCard } = methods || {};
  const isDesktop = breakpoint.isLargeDevice();
  const isTablet = breakpoint.isMediumDevice();
  if( !content || !selectedSummaryItemCard || !openOverlay ){
    return;
  }

  openOverlay( {
    onClose: () => setSelectedSummaryItemCard( null ),
    type: ( isDesktop || isTablet ) ? OVERLAY_TYPE.Modal : OVERLAY_TYPE.BottomSheet,
    alignment: 'center',
    crossButtonVisibility: true,
    disableClose:false,
    content
  } );
};

/**
 * Property type definitions
 *
 * @typedef ProductSummaryProps
 * @type {object}
 * @property {string} title - Sets the title value for product detail
 * @property {string} message - Sets the description for product detail
 * @property {string} id - Sets the id for product detail
 * @property {string} variantType - Sets the varaint type for product detail
 * @property {array} summaries - Sets the summary card details for product
 */
export const propTypes =  {
  /**
  * The prop that holds title for product details.
  */
  title: PropTypes.string,
  /**
  * The prop that holds description for product details.
  */
  message:PropTypes.string,
  /** The prop that holds summary card details for product.
  */
  summaryCards:PropTypes.array,
  /** The prop that holds id of a product.
  */
  id:PropTypes.string,
  /** The prop that holds variant type of a product.
  */
  variant:PropTypes.oneOf( ['Full', 'Half'] )
};

ProductSummary.propTypes = propTypes;

export default ProductSummary;
